import { AuthShellView, AuthStatusLine } from '@today/ui-domain'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { markForcedMfaEnrolment } from '@/auth/forced-mfa-enrolment'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { SECURITY_NAMESPACE } from '@/i18n'
import { isMfaEnrolmentAvailable, mfaEnrolmentService } from '@/services/mfa-enrolment.service'

/**
 * `/auth/mfa/enrol` — the forced-enrolment entry (TO-591). A user who is
 * required to have MFA but has not enrolled reaches it from first-login
 * (`ENROL_MFA`) or from a `403 MFA_ENROLMENT_REQUIRED` on any other surface. It
 * marks the enrolment as forced (so the shared callback sends the user to
 * re-login once the grant has revoked their session) and starts the enrolment
 * OAuth redirect — the same flow Settings uses. Effect-only: there is nothing to
 * interact with here, just the redirect to the enrolment client.
 */
export function MfaEnrolPage() {
	const { t } = useTranslation(SECURITY_NAMESPACE)
	const logo = useAuthLogo()
	const available = isMfaEnrolmentAvailable()
	// The redirect is single-use; React StrictMode double-invokes effects in dev.
	const started = useRef(false)

	useEffect(() => {
		// Without a configured enrolment client, `start()` would build an
		// `/oauth2/authorize?client_id=&…` URL and redirect to a dead Cognito page
		// (looks like a silent bounce). Guard on availability and show a real message
		// instead of starting a broken redirect.
		if (!available || started.current) return
		started.current = true
		markForcedMfaEnrolment()
		void mfaEnrolmentService.start()
	}, [available])

	return (
		<AuthShellView logo={logo} title={t('enrolment.title')}>
			{available ? (
				<AuthStatusLine text={t('enrolment.redirecting')} />
			) : (
				<p role="alert" className="text-center text-ds-sm text-ds-text-muted">
					{t('enrolment.unavailable')}
				</p>
			)}
		</AuthShellView>
	)
}
